import React from 'react';
import { FP610, FP611 } from '../../../services/p251Api';
import { fmt } from '../helpers';

interface Tab2Props {
  formData: Partial<FP610>;
  details: Partial<FP611>[];
  selectedRow: number;
  isEditing: boolean;
  isPCS: boolean;
  setSelectedRow: (idx: number) => void;
  setField: (key: keyof FP610, value: FP610[keyof FP610]) => void;
  setDetailFieldWithCalc: (rowIdx: number, key: keyof FP611, value: FP611[keyof FP611]) => void;
  onAddDetail: () => void;
  onDeleteDetail: () => void;
  onInsertDetail: () => void;
  onGWBlur: (rowIdx: number) => void;
  onMTSBlur: (rowIdx: number) => void;
  onWeightVarBlur: (rowIdx: number) => void;
  onMTSRateChange: () => void;
  onGWRateChange: () => void;
}

const Tab2Detail: React.FC<Tab2Props> = React.memo(({
  formData, details, selectedRow, isEditing, isPCS,
  setSelectedRow, setField, setDetailFieldWithCalc,
  onAddDetail, onDeleteDetail, onInsertDetail,
  onGWBlur, onMTSBlur, onWeightVarBlur,
  onMTSRateChange, onGWRateChange,
}) => {
  const ydsLabel = isPCS ? 'PCS' : 'YDS';

  return (
    <div>
      {/* Detail toolbar */}
      <div className="table-toolbar">
        <div className="left">
          <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.65)' }}>
            總項數: <strong>{formData.fp610_16 || 0}</strong>
          </span>
        </div>
        <div className="right">
          <button className="btn btn-sm" disabled={!isEditing} onClick={onInsertDetail}>
            明細插入
          </button>
          <button className="btn btn-sm btn-primary" disabled={!isEditing} onClick={onAddDetail}>
            明細新增
          </button>
          <button className="btn btn-sm btn-danger" disabled={!isEditing || details.length <= 1} onClick={onDeleteDetail}>
            明細刪除
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="table-wrapper">
        <div className="table-scroll" style={{ maxHeight: 340 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 33 }}>項數</th>
                <th style={{ width: 46 }}>R/NO</th>
                <th style={{ width: 127 }}>COLOR</th>
                <th>碼數/缸號-1</th>
                <th>碼數/缸號-2</th>
                <th>碼數/缸號-3</th>
                <th>碼數/缸號-4</th>
              </tr>
            </thead>
            <tbody>
              {details.map((row, idx) => (
                <tr
                  key={idx}
                  className={idx === selectedRow ? 'selected' : ''}
                  onClick={() => setSelectedRow(idx)}
                >
                  <td className="num">{row.fp611_02 || idx + 1}</td>
                  <td>
                    <input
                      type="text"
                      maxLength={4}
                      value={row.fp611_11 || ''}
                      onChange={e => setDetailFieldWithCalc(idx, 'fp611_11', e.target.value)}
                      disabled={!isEditing}
                      style={{ width: 40 }}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      maxLength={40}
                      value={row.fp611_12 || ''}
                      onChange={e => setDetailFieldWithCalc(idx, 'fp611_12', e.target.value)}
                      disabled={!isEditing}
                      style={{ width: 120 }}
                    />
                  </td>
                  {/* Yard/Supplement/Lot group 1 */}
                  <td>
                    <div className="yard-group">
                      <div className="yard-row">
                        <span className="yard-label">碼</span>
                        <input
                          type="number"
                          step="0.1"
                          value={row.fp611_21 || ''}
                          onChange={e => setDetailFieldWithCalc(idx, 'fp611_21', parseFloat(e.target.value) || 0)}
                          disabled={!isEditing}
                        />
                      </div>
                      <div className="yard-row">
                        <span className="yard-label">補</span>
                        <input
                          type="number"
                          step="0.1"
                          value={row.fp611_51 || ''}
                          onChange={e => setDetailFieldWithCalc(idx, 'fp611_51', parseFloat(e.target.value) || 0)}
                          disabled={!isEditing}
                        />
                      </div>
                      <div className="yard-row">
                        <span className="yard-label">缸</span>
                        <input
                          type="text"
                          maxLength={10}
                          value={row.fp611_31 || ''}
                          onChange={e => setDetailFieldWithCalc(idx, 'fp611_31', e.target.value)}
                          disabled={!isEditing}
                        />
                      </div>
                    </div>
                  </td>
                  {/* Yard/Supplement/Lot group 2 */}
                  <td>
                    <div className="yard-group">
                      <div className="yard-row">
                        <span className="yard-label">碼</span>
                        <input
                          type="number"
                          step="0.1"
                          value={row.fp611_22 || ''}
                          onChange={e => setDetailFieldWithCalc(idx, 'fp611_22', parseFloat(e.target.value) || 0)}
                          disabled={!isEditing}
                        />
                      </div>
                      <div className="yard-row">
                        <span className="yard-label">補</span>
                        <input
                          type="number"
                          step="0.1"
                          value={row.fp611_52 || ''}
                          onChange={e => setDetailFieldWithCalc(idx, 'fp611_52', parseFloat(e.target.value) || 0)}
                          disabled={!isEditing}
                        />
                      </div>
                      <div className="yard-row">
                        <span className="yard-label">缸</span>
                        <input
                          type="text"
                          maxLength={10}
                          value={row.fp611_32 || ''}
                          onChange={e => setDetailFieldWithCalc(idx, 'fp611_32', e.target.value)}
                          disabled={!isEditing}
                        />
                      </div>
                    </div>
                  </td>
                  {/* Yard/Supplement/Lot group 3 */}
                  <td>
                    <div className="yard-group">
                      <div className="yard-row">
                        <span className="yard-label">碼</span>
                        <input
                          type="number"
                          step="0.1"
                          value={row.fp611_23 || ''}
                          onChange={e => setDetailFieldWithCalc(idx, 'fp611_23', parseFloat(e.target.value) || 0)}
                          disabled={!isEditing}
                        />
                      </div>
                      <div className="yard-row">
                        <span className="yard-label">補</span>
                        <input
                          type="number"
                          step="0.1"
                          value={row.fp611_53 || ''}
                          onChange={e => setDetailFieldWithCalc(idx, 'fp611_53', parseFloat(e.target.value) || 0)}
                          disabled={!isEditing}
                        />
                      </div>
                      <div className="yard-row">
                        <span className="yard-label">缸</span>
                        <input
                          type="text"
                          maxLength={10}
                          value={row.fp611_33 || ''}
                          onChange={e => setDetailFieldWithCalc(idx, 'fp611_33', e.target.value)}
                          disabled={!isEditing}
                        />
                      </div>
                    </div>
                  </td>
                  {/* Yard/Supplement/Lot group 4 */}
                  <td>
                    <div className="yard-group">
                      <div className="yard-row">
                        <span className="yard-label">碼</span>
                        <input
                          type="number"
                          step="0.1"
                          value={row.fp611_24 || ''}
                          onChange={e => setDetailFieldWithCalc(idx, 'fp611_24', parseFloat(e.target.value) || 0)}
                          disabled={!isEditing}
                        />
                      </div>
                      <div className="yard-row">
                        <span className="yard-label">補</span>
                        <input
                          type="number"
                          step="0.1"
                          value={row.fp611_54 || ''}
                          onChange={e => setDetailFieldWithCalc(idx, 'fp611_54', parseFloat(e.target.value) || 0)}
                          disabled={!isEditing}
                        />
                      </div>
                      <div className="yard-row">
                        <span className="yard-label">缸</span>
                        <input
                          type="text"
                          maxLength={10}
                          value={row.fp611_34 || ''}
                          onChange={e => setDetailFieldWithCalc(idx, 'fp611_34', e.target.value)}
                          disabled={!isEditing}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail panel below grid */}
      <div className="detail-panels">
        <div className="detail-panel">
          <div className="panel-title">碼數彙總</div>
          <div className="form-row">
            <label>{ydsLabel}</label>
            <input type="text" readOnly value={fmt(details[selectedRow]?.fp611_41, 1)} />
          </div>
          {!isPCS && (
            <div className="form-row">
              <label>MTS</label>
              <input
                type="number"
                step="0.01"
                value={details[selectedRow]?.fp611_42 ?? ''}
                onChange={e => setDetailFieldWithCalc(selectedRow, 'fp611_42', parseFloat(e.target.value) || 0)}
                onBlur={() => onMTSBlur(selectedRow)}
                disabled={!isEditing}
              />
            </div>
          )}
        </div>

        <div className="detail-panel">
          <div className="panel-title">重量</div>
          <div className="form-row">
            <label>G.W.</label>
            <input
              type="number"
              step="0.1"
              value={details[selectedRow]?.fp611_43 ?? ''}
              onChange={e => setDetailFieldWithCalc(selectedRow, 'fp611_43', parseFloat(e.target.value) || 0)}
              onBlur={() => onGWBlur(selectedRow)}
              disabled={!isEditing}
            />
          </div>
          <div className="form-row">
            <label>N.W.</label>
            <input
              type="text"
              readOnly
              value={fmt(details[selectedRow]?.fp611_44, 1)}
            />
          </div>
        </div>

        <div className="detail-panel">
          <div className="panel-title">毛重變量</div>
          <div className="form-row">
            <label>變量</label>
            <input
              type="number"
              step="0.1"
              value={details[selectedRow]?.fp611_442 ?? ''}
              onChange={e => setDetailFieldWithCalc(selectedRow, 'fp611_442', parseFloat(e.target.value) || 0)}
              onBlur={() => onWeightVarBlur(selectedRow)}
              disabled={!isEditing}
            />
          </div>
        </div>
      </div>

      {/* Rate fields (Page2 per FoxPro layout) */}
      {!isPCS && (
        <div className="detail-panels" style={{ marginTop: 8 }}>
          <div className="detail-panel">
            <div className="panel-title">換算率</div>
            <div className="form-row">
              <label>MTS換算率</label>
              <input
                type="number"
                step="0.0001"
                value={formData.fp610_22 ?? 0.9144}
                onChange={e => setField('fp610_22', parseFloat(e.target.value) || 0)}
                onBlur={onMTSRateChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-row">
              <label>布重換算率</label>
              <input
                type="number"
                step="0.0001"
                value={formData.fp610_23 ?? 0}
                onChange={e => setField('fp610_23', parseFloat(e.target.value) || 0)}
                onBlur={onGWRateChange}
                disabled={!isEditing}
              />
            </div>
            <div className="form-row">
              <label>G.W.換算率</label>
              <input
                type="number"
                step="0.0001"
                value={formData.fp610_24 ?? 0}
                onChange={e => setField('fp610_24', parseFloat(e.target.value) || 0)}
                disabled={!isEditing}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

Tab2Detail.displayName = 'Tab2Detail';
export default Tab2Detail;
